import { Controller } from "@hotwired/stimulus"
import { Calendar } from "@fullcalendar/core"
import dayGridPlugin from "@fullcalendar/daygrid"
import timeGridPlugin from "@fullcalendar/timegrid"
import interactionPlugin from "@fullcalendar/interaction"
import luxonPlugin from "@fullcalendar/luxon3"
import idLocale from "@fullcalendar/core/locales/id"

export default class extends Controller {
  static targets = ["container"]
  static values = {
    eventsUrl: String,
    bookingUrl: String,
    newBookingUrl: String,
    timezone: String,
    opensAt: { type: String, default: "09:00" },
    closesAt: { type: String, default: "21:00" },
    initialView: { type: String, default: "" },
    readOnly: { type: Boolean, default: false }
  }

  connect() {
    // Clear any stale FullCalendar DOM from Turbo's page cache
    this.containerTarget.innerHTML = ""
    this.isMobile = window.innerWidth < 768

    this.calendar = new Calendar(this.containerTarget, {
      plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin, luxonPlugin],
      locale: idLocale,
      initialView: this.initialViewValue || (this.isMobile ? "timeGridDay" : "timeGridWeek"),
      headerToolbar: this.isMobile
        ? { left: "prev,next", center: "title", right: "today" }
        : { left: "prev,next today", center: "title", right: "dayGridMonth,timeGridWeek,timeGridDay" },
      timeZone: this.hasTimezoneValue ? this.timezoneValue : "local",
      slotMinTime: this.slotMinTime(),
      slotMaxTime: this.slotMaxTime(),
      allDaySlot: false,
      nowIndicator: true,
      editable: false,
      selectable: false,
      eventDisplay: "block",
      locale: "en-GB",
      titleFormat: this.isMobile ? { day: "numeric", month: "short" } : undefined,
      events: {
        url: this.eventsUrlValue,
        method: "GET",
        failure: () => {
          console.error("Failed to fetch calendar events")
        }
      },
      eventContent: (arg) => {
        const type = arg.event.extendedProps?.calendarType
        if (type !== "booking") return
        const props = arg.event.extendedProps
        const line = (text) => `<div style="overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${text}</div>`
        return {
          html: `<div style="padding:1px 3px;font-size:11px;line-height:1.4;overflow:hidden;height:100%;">
            ${arg.timeText ? `<div style="font-size:10px;opacity:0.8;">${arg.timeText}</div>` : ""}
            ${line(`<strong>${arg.event.title.split(" - ")[0]}</strong>`)}
            ${line(props.treatment || "")}
            ${line(props.therapist || "")}
          </div>`
        }
      },
      eventDidMount: (arg) => {
        const type = arg.event.extendedProps?.calendarType
        const el = arg.el

        if (type === "availability_slot") {
          if (arg.view.type === "dayGridMonth") return
          const count = arg.event.extendedProps.availabilityCount
          const bg = count >= 4 ? "rgba(187,247,208,0.6)" : count >= 2 ? "rgba(254,240,138,0.6)" : "rgba(220,252,231,0.6)"
          el.style.background = "transparent"
          el.style.border = "none"
          el.style.pointerEvents = "auto"
          el.style.cursor = "default"
          el.innerHTML = `<div style="display:flex;align-items:center;justify-content:center;height:100%;">
            <span style="display:inline-flex;align-items:center;justify-content:center;width:36px;height:36px;border-radius:50%;background:${bg};color:#111;font-size:24px;font-weight:700;line-height:1;">${count}</span>
          </div>`
          el.addEventListener("mouseenter", (e) => this.showTooltip([arg.event], e.clientX, e.clientY))
          el.addEventListener("mouseleave", () => this.hideTooltip())
        } else if (type !== "availability") {
          // Booking events — attach direct DOM listeners instead of FullCalendar
          // eventMouseEnter/Leave to avoid premature firing on child element transitions
          el.addEventListener("mouseenter", (e) => {
            const bookingStart = arg.event.start
            const bookingEnd = arg.event.end
            const slots = this.calendar.getEvents().filter(ev =>
              ev.extendedProps?.calendarType === "availability_slot" &&
              ev.start < bookingEnd && ev.end > bookingStart
            )
            if (slots.length > 0) this.showTooltip(slots, e.clientX, e.clientY)
          })
          el.addEventListener("mouseleave", () => this.hideTooltip())
        }
      },
      eventClick: (info) => {
        if (this.readOnlyValue) return
        if (info.event.extendedProps?.calendarType === "availability_slot") return
        if (info.event.extendedProps?.calendarType === "availability") return
        window.Turbo.visit(`${this.bookingUrlValue}/${info.event.id}`)
      },
      dateClick: (info) => {
        if (this.readOnlyValue || !this.hasNewBookingUrlValue) return
        // Format as YYYY-MM-DDTHH:MM for datetime-local input
        const dateStr = info.dateStr.slice(0, 16)
        // If clicked in month view (date only, no time), default to opening time
        const startsAt = dateStr.length <= 10 ? `${dateStr}T${this.opensAtValue}` : dateStr
        window.Turbo.visit(`${this.newBookingUrlValue}?starts_at=${startsAt}`)
      },
      height: "auto"
    })

    this.calendar.render()

    if (this.isMobile) {
      this.setupDatePicker()
    }
  }

  // Grid spans exactly the location's operating hours: opening at the top,
  // closing at the bottom. No padding rows — nothing is bookable outside these
  // hours, so a blank pre-open or post-close row would only read as "no
  // availability" at a time the spa isn't even open.
  slotMinTime() {
    const [hour] = this.opensAtValue.split(":").map(Number)
    return `${String(hour).padStart(2, "0")}:00:00`
  }

  slotMaxTime() {
    const [hour, minute] = this.closesAtValue.split(":").map(Number)
    // Round up to the next hour only when closing falls mid-hour (e.g. 21:30),
    // so the final partial slot stays visible; an on-the-hour close is exact.
    return `${String(Math.min(24, minute > 0 ? hour + 1 : hour)).padStart(2, "0")}:00:00`
  }

  showTooltip(slots, x, y) {
    clearTimeout(this._hideTimer)

    // Reuse existing tooltip element — never remove/re-add (causes flicker)
    let tooltip = document.getElementById("cal-tooltip")
    if (!tooltip) {
      tooltip = document.createElement("div")
      tooltip.id = "cal-tooltip"
      // Start off-screen and invisible to avoid flash at wrong position on first paint
      tooltip.style.cssText = "position:fixed;z-index:9999;background:white;border:1px solid #e5e7eb;border-radius:8px;padding:10px 14px;font-size:13px;box-shadow:0 4px 12px rgba(0,0,0,0.12);pointer-events:none;max-width:280px;left:-9999px;top:-9999px;opacity:0;"
      document.body.appendChild(tooltip)
    }

    slots.sort((a, b) => a.start - b.start)
    const dateLabel = slots[0].start.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" })
    const rows = slots.map(slot => {
      const names = slot.extendedProps.availableTherapists || []
      const timeLabel = slot.start.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false })
      return `<tr>
        <td style="padding:2px 8px 2px 0;color:#6b7280;white-space:nowrap;vertical-align:top;">${timeLabel}</td>
        <td style="padding:2px 0;font-weight:500;">${names.join(", ") || "—"}</td>
      </tr>`
    }).join("")
    tooltip.innerHTML = `
      <div style="font-size:11px;color:#6b7280;text-transform:uppercase;letter-spacing:0.05em;">Therapists available</div>
      <div style="font-size:12px;font-weight:600;color:#374151;margin-bottom:6px;">${dateLabel}</div>
      <table style="border-collapse:collapse;">${rows}</table>`

    this.positionTooltip(x, y)
    tooltip.style.opacity = "1"

    // Follow cursor while hovering
    if (!this._moveHandler) {
      this._moveHandler = (e) => this.positionTooltip(e.clientX, e.clientY)
      document.addEventListener("mousemove", this._moveHandler)
    }
  }

  hideTooltip() {
    // Delay prevents flicker when cursor briefly leaves then re-enters
    this._hideTimer = setTimeout(() => {
      const tip = document.getElementById("cal-tooltip")
      if (tip) tip.style.opacity = "0"
      if (this._moveHandler) {
        document.removeEventListener("mousemove", this._moveHandler)
        this._moveHandler = null
      }
    }, 120)
  }

  positionTooltip(x, y) {
    const tip = document.getElementById("cal-tooltip")
    if (!tip) return
    const offset = 16
    const padding = 8
    const { width, height } = tip.getBoundingClientRect()
    let left = x + offset
    if (left + width > window.innerWidth - padding) left = x - width - offset
    let top = y + offset
    if (top + height > window.innerHeight - padding) top = y - height - offset
    tip.style.left = `${left}px`
    tip.style.top = `${top}px`
  }

  setupDatePicker() {
    // Overlay a transparent date input on top of the title
    // so the user's tap hits the actual input (required by mobile Safari)
    const style = document.createElement("style")
    style.textContent = `
      .fc-toolbar-title { position: relative; cursor: pointer; text-decoration: underline; text-decoration-style: dotted; text-underline-offset: 4px; }
      .fc-toolbar-title .calendar-date-input { position: absolute; inset: 0; opacity: 0; width: 100%; height: 100%; cursor: pointer; -webkit-appearance: none; }
    `
    document.head.appendChild(style)

    // Watch for title changes (when navigating) and re-attach the input
    const observer = new MutationObserver(() => this.attachInputToTitle())
    observer.observe(this.containerTarget, { childList: true, subtree: true })
    this.titleObserver = observer

    // Initial attach
    requestAnimationFrame(() => this.attachInputToTitle())
  }

  attachInputToTitle() {
    const title = this.containerTarget.querySelector(".fc-toolbar-title")
    if (!title || title.querySelector(".calendar-date-input")) return

    const input = document.createElement("input")
    input.type = "date"
    input.className = "calendar-date-input"
    input.addEventListener("change", () => {
      if (input.value) {
        this.calendar.gotoDate(input.value)
      }
    })
    title.appendChild(input)
  }

  disconnect() {
    if (this.titleObserver) this.titleObserver.disconnect()
    if (this._moveHandler) document.removeEventListener("mousemove", this._moveHandler)
    clearTimeout(this._hideTimer)
    document.getElementById("cal-tooltip")?.remove()
    if (this.calendar) {
      this.calendar.destroy()
      this.calendar = null
    }
  }
}
